import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhBellSimple";

import { notificationModalStyles } from "./notification-modal.css";
import "../modal/wow-modal";
import "../notification-card/notification-card";
import type { NotificationCardData } from "../notification-card/notification-card";

@customElement("notification-modal")
export class NotificationModal extends LitElement {
  static override styles = notificationModalStyles;

  @property({ type: Boolean }) open = false;
  @property({ type: Array }) notifications: NotificationCardData[] = [];
  @property({ attribute: false }) onMarkAllRead?: () => Promise<void>;

  private async _handleClose() {
    if (this.onMarkAllRead) {
      const unreadNotifications = this.notifications.filter((n) => !n.isRead);
      if (unreadNotifications.length > 0) {
        try {
          await this.onMarkAllRead();
        } catch (error) {
          console.error("Failed to mark notifications as read:", error);
        }
      }
    }

    this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
  }

  private _renderNotificationGroup(notifications: NotificationCardData[], title: TemplateResult) {
    if (notifications.length === 0) return nothing;

    return html`
      <div class="notification-group">
        <h3 class="group-title">${title}</h3>
        <div class="group-notifications">
          ${notifications.map(
            (notification) => html`<notification-card .notification=${notification}></notification-card>`,
          )}
        </div>
      </div>
    `;
  }

  protected override render() {
    if (!this.open) return nothing;

    const unreadNotifications = this.notifications.filter((n) => !n.isRead);
    const readNotifications = this.notifications.filter((n) => n.isRead);

    return html`
      <wow-modal variant="responsive" mobile-fullscreen mobile-back-arrow @close=${this._handleClose}>
        <span slot="header" class="notifications-header"><slot name="title">Notifications</slot></span>
        <div class="notifications-container">
          ${this.notifications.length > 0
            ? html`
                <div class="notifications-list">
                  ${this._renderNotificationGroup(unreadNotifications, html`<slot name="unread">Unread</slot>`)}
                  ${this._renderNotificationGroup(readNotifications, html`<slot name="read">Read</slot>`)}
                </div>
              `
            : html`<div class="empty-state">
                <ph-bell-simple size="48"></ph-bell-simple>
                <p class="group-title"><slot name="subtitle">No notifications yet</slot></p>
              </div>`}
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "notification-modal": NotificationModal;
  }
}
